import React, { useMemo, useState, useEffect } from 'react';
import {
  UserIcon,
  ClockIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  AlertCircleIcon,
  CalendarIcon,
  ClipboardListIcon,
  DownloadIcon,
  PrinterIcon,
  BookOpenIcon,
  AwardIcon
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useData } from '../../contexts/DataContext';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
import { useToast } from '../../contexts/ToastContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { gridsService } from '../../services/gridsService';
import { StaffAllocationGrid } from '../../types/grid';
import { getTotalAllowanceMinutes, getAllowancesForSemester } from '../../types/staff';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT } from '../../types/schoolConfig';
import { resolveClassMinutes } from '../../utils/classMinutes';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import { YearSelector } from '../../components/common/YearSelector';

interface TeacherLoadDetails {
  id: string;
  name: string;
  staffCode: string;
  department: string;
  email: string;
  fte: number;
  semester1: {
    allocatedMinutes: number;
    availableMinutes: number;
    utilization: number;
    allowanceMinutes: number;
    assignments: ClassAssignment[];
  };
  semester2: {
    allocatedMinutes: number;
    availableMinutes: number;
    utilization: number;
    allowanceMinutes: number;
    assignments: ClassAssignment[];
  };
  ruahTerms?: {
    term1: ClassAssignment[];
    term2: ClassAssignment[];
    term3: ClassAssignment[];
    term4: ClassAssignment[];
    term1Minutes: number;
    term2Minutes: number;
    term3Minutes: number;
    term4Minutes: number;
  };
  overallUtilization: number;
}

interface ClassAssignment {
  className: string;
  lineId: string;
  lineName: string;
  minutes: number;
  gridName: string;
  yearLevel?: number;
  subjectCode?: string;
  semester: 'semester1' | 'semester2';
  term?: 'term1' | 'term2' | 'term3' | 'term4';
  isTermBased?: boolean;
}

const LoadSummaries: React.FC = () => {
  const { staff } = useData();
  const { addToast } = useToast();
  const { workingYear } = useAcademicYear();
  const { learningAreaNames } = useLearningAreas();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterUtilization, setFilterUtilization] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeSemester, setActiveSemester] = useState<'semester1' | 'semester2'>('semester1');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [grids, setGrids] = useState<StaffAllocationGrid[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load grid data
  useEffect(() => {
    const loadGrids = async () => {
      try {
        setIsLoading(true);
        const allGrids = await gridsService.getAllGrids();
        // Scope to the year being planned. getAllGrids() returns every year, and
        // summing them reports one teacher's 2026 and 2027 loads as a single
        // impossible total (issue #165). A grid with no academic_year is kept.
        const activeGrids = allGrids.filter(
          g => g.isActive !== false && (g.year == null || g.year === workingYear)
        );

        setGrids(activeGrids);
      } catch (error) {
        console.error('Error loading grids:', error);
        addToast('Failed to load grid data', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    loadGrids();
  }, [addToast, workingYear]);

  // Process staff data with teaching loads from grids
  const staffWithLoads = useMemo((): TeacherLoadDetails[] => {
    if (!staff || staff.length === 0 || grids.length === 0) return [];

    // Only active staff. Inactive members carry no allocations, so they rendered
    // as a wall of 0-minute / 0% rows that inflated the roster and swamped the
    // "underutilised" filter (issue #220).
    return staff.filter(teacher => teacher.status === 'active').map(teacher => {
      const teacherCode = teacher.staffCode || teacher.id;

      // Arrays to hold assignments
      const semester1Assignments: ClassAssignment[] = [];
      const semester2Assignments: ClassAssignment[] = [];

      // RUAH term arrays
      const ruahTerm1: ClassAssignment[] = [];
      const ruahTerm2: ClassAssignment[] = [];
      const ruahTerm3: ClassAssignment[] = [];
      const ruahTerm4: ClassAssignment[] = [];

      // Process each grid
      grids.forEach(grid => {
        // Detect if this is a term-based grid (RUAH)
        const isRuahGrid = grid.name?.toUpperCase().includes('RUAH') ||
                           grid.semesters?.some(sp => sp.id?.startsWith('term'));
        // FIXED: Filter out composite sub-classes to avoid double-counting
        grid.classes
          .filter(classItem => !classItem.isCompositeSubClass)
          .forEach(classItem => {
            Object.entries(classItem.lineAssignments || {}).forEach(([lineId, assignedTeacher]) => {
              // Handle both single-teacher (string) and multi-teacher (array) assignments
              let teacherMatches = false;
              let teacherMinutes = resolveClassMinutes(classItem, grid);

              if (!assignedTeacher) {
                return; // Skip null/undefined assignments
              }

              // Parse JSON string if needed (data may be stored as stringified JSON)
              let parsedTeacher = assignedTeacher;
              if (typeof assignedTeacher === 'string' && assignedTeacher.startsWith('[')) {
                try {
                  parsedTeacher = JSON.parse(assignedTeacher);
                } catch (e) {
                  console.error('Failed to parse multi-teacher JSON:', assignedTeacher);
                  return;
                }
              }

              // Check if it's a multi-teacher assignment (array format)
              if (Array.isArray(parsedTeacher)) {
                // Multi-teacher: [{"teacherCode":"AC","minutes":200}, {...}]
                const teacherAssignment = parsedTeacher.find((t: any) => {
                  const matchesCode = t.teacherCode === teacherCode;
                  const matchesId = t.teacherCode === teacher.id;
                  return matchesCode || matchesId;
                });
                if (teacherAssignment) {
                  teacherMatches = true;
                  teacherMinutes = teacherAssignment.minutes || teacherMinutes;
                }
              } else if (typeof parsedTeacher === 'string') {
                // Single teacher: "AC"
                teacherMatches = parsedTeacher === teacherCode || parsedTeacher === teacher.id;
              }

              // Check if this teacher is assigned to this class
              if (teacherMatches) {
                const line = grid.lines.find(l => l.id === lineId);
                if (line) {
                  if (isRuahGrid) {
                    // For RUAH grid, map line names to terms
                    // Line A = Term 1, Line B = Term 2, Line C = Term 3, Line D = Term 4
                    let term: 'term1' | 'term2' | 'term3' | 'term4' | undefined;
                    const lineName = line.name?.toUpperCase() || lineId.toUpperCase();

                    if (lineName.includes('A') || line.studyPeriod === 'term1') {
                      term = 'term1';
                    } else if (lineName.includes('B') || line.studyPeriod === 'term2') {
                      term = 'term2';
                    } else if (lineName.includes('C') || line.studyPeriod === 'term3') {
                      term = 'term3';
                    } else if (lineName.includes('D') || line.studyPeriod === 'term4') {
                      term = 'term4';
                    }

                    if (term) {
                      const assignment: ClassAssignment = {
                        className: classItem.className,
                        lineId: lineId,
                        lineName: line.name || `Line ${lineId}`,
                        minutes: teacherMinutes,
                        gridName: grid.name,
                        yearLevel: classItem.yearLevel,
                        subjectCode: classItem.subjectCode,
                        semester: 'semester1', // Placeholder, not used for RUAH
                        term: term,
                        isTermBased: true
                      };

                      // Route to appropriate term array based on line name
                      if (term === 'term1') {
                        ruahTerm1.push(assignment);
                      } else if (term === 'term2') {
                        ruahTerm2.push(assignment);
                      } else if (term === 'term3') {
                        ruahTerm3.push(assignment);
                      } else if (term === 'term4') {
                        ruahTerm4.push(assignment);
                      }
                    }
                  } else {
                    // Regular non-RUAH classes
                    const assignment: ClassAssignment = {
                      className: classItem.className,
                      lineId: lineId,
                      lineName: line.name || `Line ${lineId}`,
                      minutes: teacherMinutes,
                      gridName: grid.name,
                      yearLevel: classItem.yearLevel,
                      subjectCode: classItem.subjectCode,
                      semester: line.studyPeriod as 'semester1' | 'semester2',
                      isTermBased: false
                    };

                    // Route to semester arrays for regular classes
                    if (line.studyPeriod === 'semester1') {
                      semester1Assignments.push(assignment);
                    } else if (line.studyPeriod === 'semester2') {
                      semester2Assignments.push(assignment);
                    }
                  }
                }
              }
            });
          });
      });

      // Calculate RUAH term minutes
      const ruahTerm1Minutes = ruahTerm1.reduce((sum, a) => sum + a.minutes, 0);
      const ruahTerm2Minutes = ruahTerm2.reduce((sum, a) => sum + a.minutes, 0);
      const ruahTerm3Minutes = ruahTerm3.reduce((sum, a) => sum + a.minutes, 0);
      const ruahTerm4Minutes = ruahTerm4.reduce((sum, a) => sum + a.minutes, 0);

      // Calculate semester loads (regular classes only)
      const semester1RegularMinutes = semester1Assignments.reduce((sum, a) => sum + a.minutes, 0);
      const semester2RegularMinutes = semester2Assignments.reduce((sum, a) => sum + a.minutes, 0);

      // Add counted RUAH terms to semester totals
      // Term 1 RUAH → Semester 1, Term 3 RUAH → Semester 2
      const semester1AllocatedMinutes = semester1RegularMinutes + ruahTerm1Minutes;
      const semester2AllocatedMinutes = semester2RegularMinutes + ruahTerm3Minutes;

      const semester1AllowanceMinutes = getTotalAllowanceMinutes(teacher, 'semester1', workingYear);
      const semester2AllowanceMinutes = getTotalAllowanceMinutes(teacher, 'semester2', workingYear);

      // Use the staff member's own base rather than the school-wide constant.
      // 15 staff carry a non-default base (1500 / 1200 / 900) and were being
      // measured against 2220, understating their utilisation. Matches the
      // convention already used in AllocationContext.tsx and WorkloadEquity.tsx:
      // round(base * fte) - allowances.
      const baseMinutesPerSemester =
        teacher.fte * (teacher.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT);
      const semester1AvailableMinutes = baseMinutesPerSemester - semester1AllowanceMinutes;
      const semester2AvailableMinutes = baseMinutesPerSemester - semester2AllowanceMinutes;

      const semester1Utilization = semester1AvailableMinutes > 0
        ? Math.round((semester1AllocatedMinutes / semester1AvailableMinutes) * 100)
        : 0;
      const semester2Utilization = semester2AvailableMinutes > 0
        ? Math.round((semester2AllocatedMinutes / semester2AvailableMinutes) * 100)
        : 0;

      const overallUtilization = Math.round((semester1Utilization + semester2Utilization) / 2);

      // Only include ruahTerms if there are any RUAH assignments
      const hasRuahAssignments = ruahTerm1.length > 0 || ruahTerm2.length > 0 ||
                                 ruahTerm3.length > 0 || ruahTerm4.length > 0;

      return {
        id: teacher.id,
        name: teacher.name,
        staffCode: teacher.staffCode || 'UNK',
        department: teacher.department,
        email: teacher.email,
        fte: teacher.fte,
        semester1: {
          allocatedMinutes: semester1AllocatedMinutes,
          availableMinutes: semester1AvailableMinutes,
          utilization: semester1Utilization,
          allowanceMinutes: semester1AllowanceMinutes,
          assignments: semester1Assignments
        },
        semester2: {
          allocatedMinutes: semester2AllocatedMinutes,
          availableMinutes: semester2AvailableMinutes,
          utilization: semester2Utilization,
          allowanceMinutes: semester2AllowanceMinutes,
          assignments: semester2Assignments
        },
        ruahTerms: hasRuahAssignments ? {
          term1: ruahTerm1,
          term2: ruahTerm2,
          term3: ruahTerm3,
          term4: ruahTerm4,
          term1Minutes: ruahTerm1Minutes,
          term2Minutes: ruahTerm2Minutes,
          term3Minutes: ruahTerm3Minutes,
          term4Minutes: ruahTerm4Minutes
        } : undefined,
        overallUtilization
      };
    });
  }, [staff, grids, workingYear]);

  // Filter staff based on search and filters
  const filteredStaff = useMemo(() => {
    return staffWithLoads.filter(teacher => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          teacher.name.toLowerCase().includes(query) ||
          teacher.email.toLowerCase().includes(query) ||
          teacher.staffCode.toLowerCase().includes(query) ||
          teacher.department.toLowerCase().includes(query);

        if (!matchesSearch) return false;
      }

      // Department filter
      if (filterDepartment !== 'all' && teacher.department !== filterDepartment) {
        return false;
      }

      // Utilization filter
      const semesterLoad = activeSemester === 'semester1' ? teacher.semester1 : teacher.semester2;
      if (filterUtilization === 'underutilized' && semesterLoad.utilization >= 95) {
        return false;
      }
      if (filterUtilization === 'optimal' && (semesterLoad.utilization < 95 || semesterLoad.utilization > 105)) {
        return false;
      }
      if (filterUtilization === 'overloaded' && semesterLoad.utilization <= 105) {
        return false;
      }

      return true;
    });
  }, [staffWithLoads, searchQuery, filterDepartment, filterUtilization, activeSemester]);

  // Sort staff based on selected criteria
  const sortedStaff = useMemo(() => {
    return [...filteredStaff].sort((a, b) => {
      let compareA: any, compareB: any;

      switch (sortBy) {
        case 'name':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'department':
          compareA = a.department.toLowerCase();
          compareB = b.department.toLowerCase();
          break;
        case 'utilization':
          compareA = activeSemester === 'semester1' ? a.semester1.utilization : a.semester2.utilization;
          compareB = activeSemester === 'semester1' ? b.semester1.utilization : b.semester2.utilization;
          break;
        case 'fte':
          compareA = a.fte;
          compareB = b.fte;
          break;
        case 'classes':
          compareA = activeSemester === 'semester1' ? a.semester1.assignments.length : a.semester2.assignments.length;
          compareB = activeSemester === 'semester1' ? b.semester1.assignments.length : b.semester2.assignments.length;
          break;
        default:
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
      }

      if (compareA === compareB) {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      }

      if (typeof compareA === 'string' && typeof compareB === 'string') {
        return compareA > compareB
          ? (sortDirection === 'asc' ? 1 : -1)
          : (sortDirection === 'asc' ? -1 : 1);
      }

      return sortDirection === 'asc'
        ? (compareA as number) - (compareB as number)
        : (compareB as number) - (compareA as number);
    });
  }, [filteredStaff, sortBy, sortDirection, activeSemester]);

  // Toggle sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Toggle row expansion
  const toggleRow = (teacherId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(teacherId)) {
      newExpanded.delete(teacherId);
    } else {
      newExpanded.add(teacherId);
    }
    setExpandedRows(newExpanded);
  };

  // Get utilization background color
  const getUtilizationBgColor = (utilization: number) => {
    if (utilization > 105) return 'bg-danger-bg text-danger-fg';
    if (utilization >= 95) return 'bg-ok-bg text-ok-fg';
    if (utilization >= 85) return 'bg-warn-bg text-warn-fg';
    return 'bg-navy-100 text-navy-800';
  };

  // Get utilization bar color
  const getUtilizationBarColor = (utilization: number) => {
    if (utilization > 105) return 'bg-danger-solid';
    if (utilization >= 95) return 'bg-ok-solid';
    if (utilization >= 85) return 'bg-warn-solid';
    return 'bg-navy-500';
  };

  // Export load summaries
  const exportLoadSummaries = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `teaching-load-summaries-${activeSemester}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    addToast('Load summaries exported successfully', 'success');
  };

  // Generate CSV data
  const generateCSV = () => {
    const headers = [
      'Name',
      'Staff Code',
      'Learning Area',
      'FTE',
      'Semester',
      'Classes',
      'Allocated (min)',
      'Available (min)',
      'Utilization (%)',
      'Status',
      'RUAH Term 1 (min)',
      'RUAH Term 2 (min)',
      'RUAH Term 3 (min)',
      'RUAH Term 4 (min)',
      'RUAH Counted (min)',
      'RUAH Counted Terms'
    ];
    const rows = sortedStaff.map(teacher => {
      const semesterData = activeSemester === 'semester1' ? teacher.semester1 : teacher.semester2;
      const status = semesterData.utilization > 105 ? 'Overloaded' : semesterData.utilization >= 95 ? 'Optimal' : 'Underutilized';

      // Calculate RUAH term minutes
      const ruahTerm1Minutes = teacher.ruahTerms?.term1Minutes || 0;
      const ruahTerm2Minutes = teacher.ruahTerms?.term2Minutes || 0;
      const ruahTerm3Minutes = teacher.ruahTerms?.term3Minutes || 0;
      const ruahTerm4Minutes = teacher.ruahTerms?.term4Minutes || 0;
      const ruahCountedMinutes = ruahTerm1Minutes + ruahTerm3Minutes;
      const ruahCountedTerms = [
        ruahTerm1Minutes > 0 ? 'Term 1' : null,
        ruahTerm3Minutes > 0 ? 'Term 3' : null
      ].filter(Boolean).join(' + ') || 'None';

      return [
        teacher.name,
        teacher.staffCode,
        teacher.department,
        teacher.fte.toFixed(2),
        activeSemester === 'semester1' ? 'Semester 1' : 'Semester 2',
        semesterData.assignments.length,
        semesterData.allocatedMinutes,
        semesterData.availableMinutes,
        semesterData.utilization,
        status,
        ruahTerm1Minutes,
        ruahTerm2Minutes,
        ruahTerm3Minutes,
        ruahTerm4Minutes,
        ruahCountedMinutes,
        ruahCountedTerms
      ].join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  };

  // Print load summaries
  const printLoadSummaries = () => {
    window.print();
    addToast('Print dialog opened', 'info');
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Teaching Load Summaries"
          description="View and analyze teaching load distribution across staff"
        />
        <Card>
          <div className="p-12 flex justify-center">
            <Spinner size="lg" label="Loading teaching load data…" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="print:p-4">
      <PageHeader
        title="Teaching Load Summaries"
        description={`Teaching load distribution across staff for ${workingYear}`}
        actions={
          <div className="flex items-center space-x-2 print:hidden">
            <YearSelector showGridCount={false} />
            <Button variant="secondary" leftIcon={<DownloadIcon className="h-4 w-4" />} onClick={exportLoadSummaries}>
              Export
            </Button>
            <Button variant="secondary" leftIcon={<PrinterIcon className="h-4 w-4" />} onClick={printLoadSummaries}>
              Print
            </Button>
          </div>
        }
      />

      {/* Allocations and allowances are both year-scoped as of v2.11.0. FTE and
          base minutes are NOT — a single current value per person, no year
          dimension in the schema. Say so, so an earlier year is not read as a
          faithful historical record. Tracked in issue #221. Keep this wording in
          step with what is actually year-scoped; it named allowances until
          v2.11.0 made them per-year. */}
      <div className="mb-4 flex items-start gap-2 rounded-md border border-info-border bg-info-bg px-4 py-3 print:hidden">
        <AlertCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-info-fg" />
        <p className="text-sm text-info-fg">
          Allocations and allowances shown are those recorded against{' '}
          <span className="font-mono tabular-nums font-semibold">{workingYear}</span>.
          Staff FTE and base minutes are not yet stored per year, so utilisation is
          measured against each person's <strong>current</strong> contracted hours.
        </p>
      </div>

      <Card>
        <div className="p-6 border-b border-ink-200 print:hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-card-title text-ink-900">
                Staff Load Distribution
              </h3>
              <p className="mt-1 text-sm text-ink-500">
                {staffWithLoads.length} active staff • {filteredStaff.length} shown
              </p>
            </div>

            {/* Semester Toggle */}
            <div className="mt-4 sm:mt-0 flex space-x-2">
              <button
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeSemester === 'semester1'
                    ? 'bg-navy-100 text-navy-800'
                    : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
                }`}
                onClick={() => setActiveSemester('semester1')}
              >
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Semester 1
              </button>
              <button
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeSemester === 'semester2'
                    ? 'bg-navy-100 text-navy-800'
                    : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
                }`}
                onClick={() => setActiveSemester('semester2')}
              >
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Semester 2
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-ink-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                placeholder="Search staff by name, email, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex space-x-2">
              <select
                className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
              >
                <option value="all">All Learning Areas</option>
                {[
                  ...learningAreaNames,
                  ...(filterDepartment !== 'all' && !learningAreaNames.includes(filterDepartment)
                    ? [filterDepartment]
                    : []),
                ].map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>

              <select
                className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md"
                value={filterUtilization}
                onChange={(e) => setFilterUtilization(e.target.value)}
              >
                <option value="all">All Utilization</option>
                <option value="underutilized">Underutilized (&lt;95%)</option>
                <option value="optimal">Optimal (95-105%)</option>
                <option value="overloaded">Overloaded (&gt;105%)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="p-6 border-b border-ink-200 bg-ink-50 print:bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-ink-500">Staff Members</div>
                <UserIcon className="h-5 w-5 text-navy-500" />
              </div>
              <div className="mt-2 text-3xl font-semibold text-ink-900 font-mono tabular-nums">
                {staff.length}
              </div>
              <div className="mt-1 text-sm text-ink-500">
                {staff.filter((s) => s.fte === 1).length} full-time •{' '}
                {staff.filter((s) => s.fte < 1).length} part-time
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-ink-500">Average Utilization</div>
                <ClockIcon className="h-5 w-5 text-ok-fg" />
              </div>
              <div className="mt-2 text-3xl font-semibold text-ink-900 font-mono tabular-nums">
                {Math.round(
                  sortedStaff.reduce(
                    (sum, teacher) =>
                      sum +
                      (activeSemester === 'semester1'
                        ? teacher.semester1.utilization
                        : teacher.semester2.utilization),
                    0
                  ) / (sortedStaff.length || 1)
                )}%
              </div>
              <div className="mt-1 text-sm text-ink-500">
                For {activeSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-ink-500">Overloaded Staff</div>
                <AlertCircleIcon className="h-5 w-5 text-danger-fg" />
              </div>
              <div className="mt-2 text-3xl font-semibold text-ink-900 font-mono tabular-nums">
                {
                  sortedStaff.filter((teacher) =>
                    activeSemester === 'semester1'
                      ? teacher.semester1.utilization > 105
                      : teacher.semester2.utilization > 105
                  ).length
                }
              </div>
              <div className="mt-1 text-sm text-ink-500">Staff with &gt;105% utilization</div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-ink-500">Underutilized Staff</div>
                <ClipboardListIcon className="h-5 w-5 text-warn-fg" />
              </div>
              <div className="mt-2 text-3xl font-semibold text-ink-900 font-mono tabular-nums">
                {
                  sortedStaff.filter((teacher) =>
                    activeSemester === 'semester1'
                      ? teacher.semester1.utilization < 95
                      : teacher.semester2.utilization < 95
                  ).length
                }
              </div>
              <div className="mt-1 text-sm text-ink-500">Staff with &lt;95% utilization</div>
            </div>
          </div>
        </div>

        {/* Staff Load Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200 table-fixed">
            <thead className="bg-ink-50">
              <tr>
                <th scope="col" className="w-8 px-2 py-3 print:hidden"></th>
                <th
                  scope="col"
                  className="w-[22%] px-3 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100 print:hover:bg-ink-50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    <span>Staff</span>
                    {sortBy === 'name' &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      ))}
                  </div>
                </th>
                <th
                  scope="col"
                  className="w-16 px-2 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100 print:hover:bg-ink-50"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center">
                    <span>Learning Area</span>
                    {sortBy === 'department' &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      ))}
                  </div>
                </th>
                <th
                  scope="col"
                  className="w-14 px-2 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100 print:hover:bg-ink-50"
                  onClick={() => handleSort('fte')}
                >
                  <div className="flex items-center justify-center">
                    <span>FTE</span>
                    {sortBy === 'fte' &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      ))}
                  </div>
                </th>
                <th scope="col" className="w-16 px-2 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                  <span>Allow.</span>
                </th>
                <th
                  scope="col"
                  className="w-[15%] px-2 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100 print:hover:bg-ink-50"
                  onClick={() => handleSort('utilization')}
                >
                  <div className="flex items-center justify-center">
                    <span>Sem 1</span>
                    {sortBy === 'utilization' && activeSemester === 'semester1' &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      ))}
                  </div>
                </th>
                <th
                  scope="col"
                  className="w-[15%] px-2 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100 print:hover:bg-ink-50"
                  onClick={() => handleSort('utilization')}
                >
                  <div className="flex items-center justify-center">
                    <span>Sem 2</span>
                    {sortBy === 'utilization' && activeSemester === 'semester2' &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      ))}
                  </div>
                </th>
                <th scope="col" className="w-[15%] px-2 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                  <span>Minutes</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {sortedStaff.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4">
                    {/* With a year selector on the page it is now possible to
                        land on a year that has no grids. Say that, rather than
                        blaming the search box. */}
                    {grids.length === 0 ? (
                      <EmptyState
                        icon={<CalendarIcon className="h-6 w-6" />}
                        title={`No grids for ${workingYear}`}
                        description="Choose a different year, or build the grids for this year in the Timetable Editor."
                      />
                    ) : (
                      <EmptyState
                        icon={<UserIcon className="h-6 w-6" />}
                        title="No staff members found"
                        description="Try adjusting your search or filters"
                      />
                    )}
                  </td>
                </tr>
              ) : (
                sortedStaff.map((teacher) => {
                  const semesterData = activeSemester === 'semester1' ? teacher.semester1 : teacher.semester2;
                  const isExpanded = expandedRows.has(teacher.id);
                  const teacherAllowances = getAllowancesForSemester(
                    staff.find(s => s.id === teacher.id)!,
                    activeSemester,
                    workingYear
                  );

                  return (
                    <React.Fragment key={teacher.id}>
                      <tr className="hover:bg-ink-50">
                        <td className="px-2 py-3 print:hidden">
                          <button
                            onClick={() => toggleRow(teacher.id)}
                            className="text-ink-400 hover:text-ink-500"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center">
                            <div className="h-8 w-8 flex-shrink-0 mr-2">
                              <div className="h-8 w-8 rounded-full bg-navy-100 flex items-center justify-center">
                                <span className="text-navy-600 font-medium text-xs">
                                  {teacher.name
                                    ?.split(' ')
                                    .map((n: string) => n[0])
                                    .join('') || '??'}
                                </span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-ink-900 text-sm truncate">{teacher.name}</div>
                              <div className="text-ink-500 text-xs flex items-center">
                                <span className="font-mono tabular-nums bg-ink-100 px-1 py-0.5 text-xs rounded">
                                  {teacher.staffCode}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-xs text-ink-500 truncate" title={displayLearningArea(teacher.department)}>
                          {displayLearningArea(teacher.department)}
                        </td>
                        <td className="px-2 py-3 text-sm text-ink-500 text-center font-mono tabular-nums">
                          {teacher.fte.toFixed(2)}
                        </td>
                        <td className="px-2 py-3 text-center">
                          {(teacher.semester1.allowanceMinutes > 0 || teacher.semester2.allowanceMinutes > 0) ? (
                            <div className="flex flex-col items-center space-y-0.5">
                              <span className="text-navy-600 font-medium text-xs font-mono tabular-nums">
                                {teacher.semester1.allowanceMinutes}
                              </span>
                              <span className="text-navy-600 font-medium text-xs font-mono tabular-nums">
                                {teacher.semester2.allowanceMinutes}
                              </span>
                            </div>
                          ) : (
                            <span className="text-ink-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex flex-col items-center space-y-1">
                            <div className="flex items-center w-full">
                              <span
                                className={`px-1.5 py-0.5 text-xs rounded font-medium whitespace-nowrap font-mono tabular-nums ${getUtilizationBgColor(
                                  teacher.semester1.utilization
                                )}`}
                              >
                                {teacher.semester1.utilization}%
                              </span>
                              <div className="ml-1 flex-1 bg-ink-200 rounded-full h-1.5 overflow-hidden">
                                {/* Teaching time bar */}
                                <div
                                  className={`h-1.5 float-left ${getUtilizationBarColor(
                                    teacher.semester1.utilization
                                  )}`}
                                  style={{
                                    width: `${Math.min(100, (teacher.semester1.allocatedMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100)}%`,
                                  }}
                                />
                                {/* Allowance bar */}
                                {teacher.semester1.allowanceMinutes > 0 && (
                                  <div
                                    className="h-1.5 float-left bg-navy-500"
                                    style={{
                                      width: `${Math.min(100 - (teacher.semester1.allocatedMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100, (teacher.semester1.allowanceMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100)}%`,
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-ink-500 font-mono tabular-nums">
                              {teacher.semester1.assignments.length} classes
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex flex-col items-center space-y-1">
                            <div className="flex items-center w-full">
                              <span
                                className={`px-1.5 py-0.5 text-xs rounded font-medium whitespace-nowrap font-mono tabular-nums ${getUtilizationBgColor(
                                  teacher.semester2.utilization
                                )}`}
                              >
                                {teacher.semester2.utilization}%
                              </span>
                              <div className="ml-1 flex-1 bg-ink-200 rounded-full h-1.5 overflow-hidden">
                                {/* Teaching time bar */}
                                <div
                                  className={`h-1.5 float-left ${getUtilizationBarColor(
                                    teacher.semester2.utilization
                                  )}`}
                                  style={{
                                    width: `${Math.min(100, (teacher.semester2.allocatedMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100)}%`,
                                  }}
                                />
                                {/* Allowance bar */}
                                {teacher.semester2.allowanceMinutes > 0 && (
                                  <div
                                    className="h-1.5 float-left bg-navy-500"
                                    style={{
                                      width: `${Math.min(100 - (teacher.semester2.allocatedMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100, (teacher.semester2.allowanceMinutes / (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT)) * 100)}%`,
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-ink-500 font-mono tabular-nums">
                              {teacher.semester2.assignments.length} classes
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex flex-col items-center space-y-0.5 text-xs">
                            <div className="flex items-center justify-between w-full">
                              <span className="text-ink-500">S1:</span>
                              <span className="font-medium text-ink-900 font-mono tabular-nums">{teacher.semester1.allocatedMinutes}/{teacher.semester1.availableMinutes}</span>
                            </div>
                            <div className="flex items-center justify-between w-full">
                              <span className="text-ink-500">S2:</span>
                              <span className="font-medium text-ink-900 font-mono tabular-nums">{teacher.semester2.allocatedMinutes}/{teacher.semester2.availableMinutes}</span>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row - Class Details */}
                      {isExpanded && (
                        <tr className="bg-ink-50">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="ml-12">
                              {/* Allowances Section - moved to top */}
                              {teacherAllowances.length > 0 && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-ink-900 mb-2 flex items-center">
                                    <AwardIcon className="h-4 w-4 mr-2 text-navy-600" />
                                    Leadership Allowances
                                  </h5>
                                  <div className="bg-navy-50 rounded-lg border border-navy-200 p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {teacherAllowances.map((allowance, idx) => (
                                        <div key={idx} className="flex justify-between items-center">
                                          <span className="text-sm text-navy-900">{allowance.name}</span>
                                          <span className="text-sm font-medium text-navy-700 font-mono tabular-nums">
                                            {allowance.minutes} min
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-navy-300 flex justify-between items-center">
                                      <span className="text-sm font-semibold text-navy-900">
                                        Total Allowances:
                                      </span>
                                      <span className="text-sm font-bold text-navy-700 font-mono tabular-nums">
                                        {semesterData.allowanceMinutes} min
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <h4 className="text-sm font-medium text-ink-900 mb-4 flex items-center">
                                <BookOpenIcon className="h-4 w-4 mr-2 text-navy-600" />
                                Teaching Assignments - Full Year Overview
                              </h4>

                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Semester 1 */}
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                      <CalendarIcon className="h-4 w-4 mr-1 text-navy-500" />
                                      Semester 1
                                    </h5>
                                    <span className="text-xs text-ink-500 font-mono tabular-nums">
                                      {teacher.semester1.assignments.filter(a => !a.isTermBased).length} classes
                                    </span>
                                  </div>

                                  {teacher.semester1.assignments.filter(a => !a.isTermBased).length > 0 ? (
                                    <div className="bg-white rounded-lg border-2 border-navy-200 overflow-hidden">
                                      <table className="min-w-full divide-y divide-ink-200">
                                        <thead className="bg-navy-50">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Class
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Yr
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Line
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                              Min
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-ink-200">
                                          {teacher.semester1.assignments
                                            .filter(assignment => !assignment.isTermBased)
                                            .map((assignment, idx) => (
                                            <tr key={idx} className="hover:bg-navy-50">
                                              <td className="px-3 py-2 text-sm text-ink-900">
                                                {assignment.className}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-ink-500">
                                                {assignment.yearLevel || '-'}
                                              </td>
                                              <td className="px-3 py-2 text-sm">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800 font-mono tabular-nums">
                                                  {assignment.lineId}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2 text-sm text-ink-900 text-right font-medium font-mono tabular-nums">
                                                {assignment.minutes}
                                              </td>
                                            </tr>
                                          ))}
                                          <tr className="bg-navy-100 font-semibold">
                                            <td colSpan={3} className="px-3 py-2 text-sm text-ink-900 text-right">
                                              Sem 1 Total:
                                            </td>
                                            <td className="px-3 py-2 text-sm text-ink-900 text-right font-mono tabular-nums">
                                              {teacher.semester1.allocatedMinutes}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="bg-white rounded-lg border-2 border-navy-200 p-4 text-center">
                                      <CalendarIcon className="h-6 w-6 text-ink-300 mx-auto mb-1" />
                                      <p className="text-xs text-ink-500">No classes in Semester 1</p>
                                    </div>
                                  )}

                                  {/* Semester 1 Utilization Summary */}
                                  <div className="mt-2 p-2 bg-navy-50 rounded border border-navy-200">
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-ink-700">Available:</span>
                                      <span className="font-medium font-mono tabular-nums">{teacher.semester1.availableMinutes} min</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs mt-1">
                                      <span className="text-ink-700">Utilization:</span>
                                      <span className={`font-bold font-mono tabular-nums ${
                                        teacher.semester1.utilization > 105 ? 'text-danger-fg' :
                                        teacher.semester1.utilization >= 95 ? 'text-ok-fg' :
                                        'text-warn-fg'
                                      }`}>
                                        {teacher.semester1.utilization}%
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Semester 2 */}
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                      <CalendarIcon className="h-4 w-4 mr-1 text-ok-fg" />
                                      Semester 2
                                    </h5>
                                    <span className="text-xs text-ink-500 font-mono tabular-nums">
                                      {teacher.semester2.assignments.filter(a => !a.isTermBased).length} classes
                                    </span>
                                  </div>

                                  {teacher.semester2.assignments.filter(a => !a.isTermBased).length > 0 ? (
                                    <div className="bg-white rounded-lg border-2 border-ok-border overflow-hidden">
                                      <table className="min-w-full divide-y divide-ink-200">
                                        <thead className="bg-ok-bg">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Class
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Yr
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                              Line
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                              Min
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-ink-200">
                                          {teacher.semester2.assignments
                                            .filter(assignment => !assignment.isTermBased)
                                            .map((assignment, idx) => (
                                            <tr key={idx} className="hover:bg-ok-bg">
                                              <td className="px-3 py-2 text-sm text-ink-900">
                                                {assignment.className}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-ink-500">
                                                {assignment.yearLevel || '-'}
                                              </td>
                                              <td className="px-3 py-2 text-sm">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-ok-bg text-ok-fg font-mono tabular-nums">
                                                  {assignment.lineId}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2 text-sm text-ink-900 text-right font-medium font-mono tabular-nums">
                                                {assignment.minutes}
                                              </td>
                                            </tr>
                                          ))}
                                          <tr className="bg-ok-bg font-semibold">
                                            <td colSpan={3} className="px-3 py-2 text-sm text-ink-900 text-right">
                                              Sem 2 Total:
                                            </td>
                                            <td className="px-3 py-2 text-sm text-ink-900 text-right font-mono tabular-nums">
                                              {teacher.semester2.allocatedMinutes}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="bg-white rounded-lg border-2 border-ok-border p-4 text-center">
                                      <CalendarIcon className="h-6 w-6 text-ink-300 mx-auto mb-1" />
                                      <p className="text-xs text-ink-500">No classes in Semester 2</p>
                                    </div>
                                  )}

                                  {/* Semester 2 Utilization Summary */}
                                  <div className="mt-2 p-2 bg-ok-bg rounded border border-ok-border">
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-ink-700">Available:</span>
                                      <span className="font-medium font-mono tabular-nums">{teacher.semester2.availableMinutes} min</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs mt-1">
                                      <span className="text-ink-700">Utilization:</span>
                                      <span className={`font-bold font-mono tabular-nums ${
                                        teacher.semester2.utilization > 105 ? 'text-danger-fg' :
                                        teacher.semester2.utilization >= 95 ? 'text-ok-fg' :
                                        'text-warn-fg'
                                      }`}>
                                        {teacher.semester2.utilization}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* RUAH Terms Section */}
                              {teacher.ruahTerms && (
                                <div className="mt-6">
                                  <h4 className="text-sm font-medium text-ink-900 mb-4 flex items-center">
                                    <BookOpenIcon className="h-4 w-4 mr-2 text-navy-600" />
                                    RUAH Term Assignments (Term-by-Term)
                                  </h4>

                                  <div className="bg-navy-50 rounded-lg border-2 border-navy-200 p-4 mb-3">
                                    <div className="text-xs text-navy-800">
                                      <strong>Note:</strong> RUAH Classes operate on a term-by-term basis. Your total hours are reflective of Term 1 RUAH.
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* Term 1 */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                          <CalendarIcon className="h-4 w-4 mr-1 text-navy-500" />
                                          Term 1
                                          <span className="ml-2 text-xs text-ok-fg font-bold bg-ok-bg px-2 py-0.5 rounded">
                                            COUNTED → S1
                                          </span>
                                        </h5>
                                        <span className="text-xs text-navy-600 font-medium font-mono tabular-nums">
                                          {teacher.ruahTerms.term1Minutes} min
                                        </span>
                                      </div>

                                      {teacher.ruahTerms.term1.length > 0 ? (
                                        <div className="bg-white rounded-lg border-2 border-navy-200 overflow-hidden">
                                          <table className="min-w-full divide-y divide-ink-200">
                                            <thead className="bg-navy-50">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Class
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Line
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                                  Min
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ink-200">
                                              {teacher.ruahTerms.term1.map((assignment, idx) => (
                                                <tr key={idx} className="hover:bg-navy-50">
                                                  <td className="px-3 py-2 text-sm text-ink-900">
                                                    {assignment.className}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800 font-mono tabular-nums">
                                                      {assignment.lineId}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-ink-900 text-right font-medium">
                                                    {assignment.minutes}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="bg-white rounded-lg border-2 border-navy-200 p-4 text-center">
                                          <p className="text-xs text-ink-500">No classes in Term 1</p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Term 2 */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                          <CalendarIcon className="h-4 w-4 mr-1 text-ink-400" />
                                          Term 2
                                          <span className="ml-2 text-xs text-ink-500 font-medium">
                                            Not counted
                                          </span>
                                        </h5>
                                        <span className="text-xs text-ink-500 font-medium font-mono tabular-nums">
                                          {teacher.ruahTerms.term2Minutes} min
                                        </span>
                                      </div>

                                      {teacher.ruahTerms.term2.length > 0 ? (
                                        <div className="bg-white rounded-lg border-2 border-ink-300 overflow-hidden opacity-75">
                                          <table className="min-w-full divide-y divide-ink-200">
                                            <thead className="bg-ink-50">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Class
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Line
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                                  Min
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ink-200">
                                              {teacher.ruahTerms.term2.map((assignment, idx) => (
                                                <tr key={idx}>
                                                  <td className="px-3 py-2 text-sm text-ink-500">
                                                    {assignment.className}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-ink-100 text-ink-500 font-mono tabular-nums">
                                                      {assignment.lineId}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-ink-500 text-right font-medium font-mono tabular-nums">
                                                    {assignment.minutes}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="bg-white rounded-lg border-2 border-ink-300 p-4 text-center opacity-75">
                                          <p className="text-xs text-ink-500">No classes in Term 2</p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Term 3 */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                          <CalendarIcon className="h-4 w-4 mr-1 text-navy-500" />
                                          Term 3
                                          <span className="ml-2 text-xs text-ok-fg font-bold bg-ok-bg px-2 py-0.5 rounded">
                                            COUNTED → S2
                                          </span>
                                        </h5>
                                        <span className="text-xs text-navy-600 font-medium font-mono tabular-nums">
                                          {teacher.ruahTerms.term3Minutes} min
                                        </span>
                                      </div>

                                      {teacher.ruahTerms.term3.length > 0 ? (
                                        <div className="bg-white rounded-lg border-2 border-navy-200 overflow-hidden">
                                          <table className="min-w-full divide-y divide-ink-200">
                                            <thead className="bg-navy-50">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Class
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Line
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                                  Min
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ink-200">
                                              {teacher.ruahTerms.term3.map((assignment, idx) => (
                                                <tr key={idx} className="hover:bg-navy-50">
                                                  <td className="px-3 py-2 text-sm text-ink-900">
                                                    {assignment.className}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800 font-mono tabular-nums">
                                                      {assignment.lineId}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-ink-900 text-right font-medium">
                                                    {assignment.minutes}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="bg-white rounded-lg border-2 border-navy-200 p-4 text-center">
                                          <p className="text-xs text-ink-500">No classes in Term 3</p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Term 4 */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm font-semibold text-ink-700 flex items-center">
                                          <CalendarIcon className="h-4 w-4 mr-1 text-ink-400" />
                                          Term 4
                                          <span className="ml-2 text-xs text-ink-500 font-medium">
                                            Not counted
                                          </span>
                                        </h5>
                                        <span className="text-xs text-ink-500 font-medium font-mono tabular-nums">
                                          {teacher.ruahTerms.term4Minutes} min
                                        </span>
                                      </div>

                                      {teacher.ruahTerms.term4.length > 0 ? (
                                        <div className="bg-white rounded-lg border-2 border-ink-300 overflow-hidden opacity-75">
                                          <table className="min-w-full divide-y divide-ink-200">
                                            <thead className="bg-ink-50">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Class
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase">
                                                  Line
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500 uppercase">
                                                  Min
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ink-200">
                                              {teacher.ruahTerms.term4.map((assignment, idx) => (
                                                <tr key={idx}>
                                                  <td className="px-3 py-2 text-sm text-ink-500">
                                                    {assignment.className}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-ink-100 text-ink-500 font-mono tabular-nums">
                                                      {assignment.lineId}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-ink-500 text-right font-medium font-mono tabular-nums">
                                                    {assignment.minutes}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="bg-white rounded-lg border-2 border-ink-300 p-4 text-center opacity-75">
                                          <p className="text-xs text-ink-500">No classes in Term 4</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Semester 1 Total */}
                              <div className="mt-6 p-3 bg-navy-50 rounded-lg border-2 border-navy-300">
                                <h5 className="text-sm font-semibold text-navy-900 mb-3">Semester 1 Total</h5>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="text-ink-500">Total Classes:</span>
                                    <span className="ml-2 font-bold text-ink-900 font-mono tabular-nums">
                                      {
                                        // Semester 1 regular classes (excluding term-based)
                                        teacher.semester1.assignments.filter(a => !a.isTermBased).length +
                                        // Term 1 RUAH classes (counted toward Semester 1)
                                        (teacher.ruahTerms?.term1.length || 0)
                                      }
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-ink-500">Total Minutes:</span>
                                    <span className="ml-2 font-bold text-ink-900 font-mono tabular-nums">
                                      {teacher.semester1.allocatedMinutes}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-ink-500">S1 Utilization:</span>
                                    <span className={`ml-2 font-bold font-mono tabular-nums ${
                                      teacher.semester1.utilization > 105 ? 'text-danger-fg' :
                                      teacher.semester1.utilization >= 95 ? 'text-ok-fg' :
                                      'text-warn-fg'
                                    }`}>
                                      {teacher.semester1.utilization}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default LoadSummaries;
